import { api } from './index';

export type PdfExportType = 'pdf' | 'print_html';
export type PdfExportStatus = 'processing' | 'completed' | 'failed';

export interface PdfExportRecord {
  id: string;
  createdAt: string;
  status: PdfExportStatus;
  exportType: PdfExportType;
  downloadUrl?: string;
  buildingId: string;
  buildingName: string;
  billingPeriod: string;
  billingPeriodDisplay: string;
  onlyOutstanding: boolean;
  onlyWithoutApp: boolean;
  apartmentGroupIds: string[];
  groupLabel?: string | null;
  feeCategoryLabel?: string | null;
  feeCategoryIds?: string[];
  noticeCount?: number | null;
  email: string;
  title: string;
  noticeIssueLabel?: string | null;
  noticeIssueDisplay?: string | null;
  requestedByName: string;
  errorMessage?: string | null;
  feeNoticeSentAt?: string | null;
  feeNoticeSendSuccessCount?: number | null;
  feeNoticeSendFailureCount?: number | null;
}

export interface PdfExportNoticeItem {
  id: string;
  code: string;
  noticeNumber: string;
  ownerName: string;
  hasApp: boolean;
  groupName?: string | null;
  createdAt: string;
  feeNoticeSentAt?: string | null;
}

export interface CreatePdfExportPayload {
  buildingId: string;
  billingPeriod: string;
  noticeIssueLabel?: string;
  onlyOutstanding: boolean;
  includeAllUnpaidOnNotice?: boolean;
  onlyWithoutApp?: boolean;
  apartmentGroupIds?: string[];
  apartmentIds?: string[];
  feeCategoryIds?: string[];
  exportType?: 'PDF' | 'PRINT_HTML';
  email?: string;
}

const getApiBase = (): string =>
  (api.defaults.baseURL || '').replace(/\/$/, '');

export const pdfExportsApi = {
  requestExport: async (payload: CreatePdfExportPayload) => {
    const response = await api.post('/pdf-exports/request', payload);
    return response.data as { message: string; id: string };
  },

  downloadZip: async (downloadUrl: string, suggestedName?: string): Promise<void> => {
    const match = downloadUrl.match(/\/pdf-exports\/download\/([^/?#]+)/);
    if (!match?.[1]) {
      throw new Error('Invalid download URL');
    }
    const filename = decodeURIComponent(match[1]);
    const response = await api.get(`/pdf-exports/download/${filename}`, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = suggestedName || filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getHistory: async (params?: {
    page?: number;
    limit?: number;
    billingPeriod?: string;
    requestedBy?: string;
    apartment?: string;
    buildingId?: string;
  }) => {
    const response = await api.get('/pdf-exports/history', {
      params: {
        page: params?.page,
        limit: params?.limit,
        billingPeriod: params?.billingPeriod || undefined,
        requestedBy: params?.requestedBy || undefined,
        apartment: params?.apartment?.trim() || undefined,
        buildingId: params?.buildingId || undefined,
      },
    });
    return response.data as {
      items: PdfExportRecord[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    };
  },

  getExport: async (exportId: string): Promise<PdfExportRecord> => {
    const response = await api.get(`/pdf-exports/${exportId}`);
    return response.data;
  },

  getNotices: async (
    exportId: string,
    params?: {
      page?: number;
      limit?: number;
      onlyWithoutApp?: boolean;
      search?: string;
    },
  ) => {
    const response = await api.get(`/pdf-exports/${exportId}/notices`, {
      params: {
        page: params?.page,
        limit: params?.limit,
        onlyWithoutApp: params?.onlyWithoutApp ? '1' : undefined,
        search: params?.search,
      },
    });
    return response.data as {
      export: PdfExportRecord;
      items: PdfExportNoticeItem[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    };
  },

  getBatchPrintHtmlUrl: (
    exportId: string,
    options?: {
      page?: number;
      perPage?: number;
      onlyWithoutApp?: boolean;
      printAll?: boolean;
      autoPrint?: boolean;
    },
  ): string => {
    const base = getApiBase();
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.perPage) params.set('perPage', String(options.perPage));
    if (options?.onlyWithoutApp) params.set('onlyWithoutApp', '1');
    if (options?.printAll) params.set('printAll', '1');
    if (options?.autoPrint) params.set('print', '1');
    const qs = params.toString();
    return `${base}/pdf-exports/${exportId}/print.html${qs ? `?${qs}` : ''}`;
  },

  getNoticeHtmlUrl: (
    exportId: string,
    apartmentId: string,
    autoPrint = false,
  ): string => {
    const base = getApiBase();
    const suffix = autoPrint ? '?print=1' : '';
    return `${base}/pdf-exports/${exportId}/notices/${apartmentId}.html${suffix}`;
  },

  sendNotice: async (
    exportId: string,
    apartmentId: string,
    payload: {
      deliveryMethods: ('EMAIL' | 'APP')[];
      overrideEmail?: string;
    },
  ) => {
    const response = await api.post(
      `/pdf-exports/${exportId}/notices/${apartmentId}/send`,
      payload,
    );
    return response.data as {
      success: boolean;
      emailsSent: number;
      appNotificationsSent: number;
      errors: string[];
    };
  },

  sendBuildingFeeNotices: async (payload: CreatePdfExportPayload & {
    deliveryMethods: ('EMAIL' | 'APP')[];
  }) => {
    const response = await api.post('/pdf-exports/send-fee-notices', payload);
    return response.data as {
      success: boolean;
      message: string;
      exportId: string;
      noticeCount: number;
      emailsSent: number;
      appNotificationsSent: number;
      successCount: number;
      failureCount: number;
      errors: string[];
    };
  },
};

export default pdfExportsApi;
